import React, {useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// Hooks
import useApi from "../../hooks/useApi.js";

// Utils
import {
  formatAddressAsString,
  getMapFromAddress,
  getMapFromCoords,
  openLink
} from "../../utils/helpers.js";
import {GROUP} from "../../utils/builder.js";

// Components
import Modal from "../../components/Modal.js";

// Style
import {pad} from "../../style/components/variables.js";
import {
  Heading,
  AnchorInline,
  Label,
  Text,
  Button,
  ButtonLoader,
  Abbr
} from "../../style/components/general.js";

const ModalHelp = ({visible, setVisible, target, conditionsSatisfied, byId}) => {
  const {api} = useApi("files");

  const [loadingFiles, setLoadingFiles] = useState({});

  const getNoteValue = value => {
    if (value?.includes("http"))
      return (
        <AnchorInline href={value} target="_blank" rel="noreferrer" data-testid="help.fileLink">
          View
        </AnchorInline>
      );
    return value;
  };

  const getMapFromLocation = () => {
    if (target?.address?.line1) return getMapFromAddress(formatAddressAsString(target.address));
    if (target?.address?.lat && target?.address?.lon)
      return getMapFromCoords(target.address.lat, target.address.lon);
    return "";
  };

  const openFile = fileId => {
    api.callGet(fileId).then(({status, data}) => {
      if (status === 200) openLink(data.link);
      setLoadingFiles(prev => ({...prev, [fileId]: false}));
    });
  };

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <ModalHeading element={target.element}>{target.label}: Support Information</ModalHeading>

      {target?.help
        ?.filter(
          ({key, value, id}) =>
            key &&
            value &&
            (!id ||
              !byId ||
              !(id in byId) ||
              (!byId[id].deleted && !byId[id].stale && byId[id]?.type !== "weather")) &&
            (!id ||
              !conditionsSatisfied ||
              !conditionsSatisfied.current ||
              conditionsSatisfied.current[id] !== false)
        )
        .map(({key, value, fileId}) => (
          <HelpItem key={key}>
            <Label bold>{key.toUpperCase()}</Label>
            {fileId ? (
              <Button
                type="button"
                onClick={() => {
                  setLoadingFiles(prev => ({...prev, [fileId]: true}));
                  openFile(fileId);
                }}
                loading={loadingFiles[fileId] ? 1 : 0}>
                Open File
                {loadingFiles[fileId] && <ButtonLoader />}
              </Button>
            ) : (
              <Text>
                <Abbr title={value}>{getNoteValue(value)}</Abbr>
              </Text>
            )}
          </HelpItem>
        ))}

      {target?.address && (
        <>
          <br />
          <Text>
            View In Maps&nbsp;
            <AnchorInline
              rel="noreferrer"
              target="_blank"
              data-testid="help.mapLink"
              href={getMapFromLocation()}>
              Location
            </AnchorInline>
          </Text>
        </>
      )}
    </Modal>
  );
};

ModalHelp.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  target: PropTypes.objectOf(PropTypes.any).isRequired,
  conditionsSatisfied: PropTypes.objectOf(PropTypes.any),
  byId: PropTypes.objectOf(PropTypes.any).isRequired
};

// Style Overrides
const ModalHeading = styled(Heading)`
  color: ${({element, theme}) => (element === GROUP ? theme.group : theme.component)};
`;

const HelpItem = styled.div`
  padding: ${pad}px 0;

  ${Label} {
    color: ${({theme}) => theme.field};
  }
`;

export default ModalHelp;
